import { useState, type InputHTMLAttributes, type ReactNode } from 'react';

/**
 * Isian kata sandi dengan tombol mata — satu komponen untuk SELURUH isian sandi Workforce: masuk,
 * kata sandi baru (pemulihan), terima undangan, dan mulai trial. Permintaan Owner 29 Okt 2026:
 * "kasih icon mata di kolom password, baik saat login ataupun saat reset password". Bentuknya
 * sama dengan CRM (`apps/web/src/components/IsianSandi.tsx`, #1911) dan AlurCare (#1909).
 *
 * Kontraknya:
 *   - sandi TERSEMBUNYI secara bawaan, dan kembali tersembunyi setiap kali isian dipasang ulang;
 *   - setiap isian punya status matanya SENDIRI — membuka "kata sandi baru" tidak ikut membuka
 *     "ulangi kata sandi";
 *   - `type="button"` WAJIB — tanpa itu tombol di dalam <form> menjadi tombol kirim, dan menekan
 *     mata langsung mengirim formulir masuk/reset;
 *   - label isian tetap milik halaman (`<label htmlFor={id}>` di LUAR komponen ini). Tombolnya
 *     tidak boleh berada di dalam <label> yang membungkus isian: nama aksesibel tombol akan ikut
 *     terbaca sebagai bagian nama isiannya ("Kata sandi baru Tampilkan kata sandi").
 *
 * Kenapa tetap ada di layar masuk: keluhan hari pertama yang paling sering adalah "email atau
 * kata sandi salah", dan sebagian besar berakhir sebagai salah ketik pada papan ketik ponsel.
 * Tanpa cara memeriksa apa yang sudah diketik, satu-satunya jalan yang tersisa adalah mengulang
 * dari awal — atau meminta tautan pemulihan untuk kata sandi yang sebenarnya sudah benar.
 */
export function IsianSandi({
  id,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'> & {
  readonly id: string;
}): ReactNode {
  const [tampil, setTampil] = useState(false);
  return (
    <div className="isian-sandi">
      <input
        {...props}
        id={id}
        type={tampil ? 'text' : 'password'}
        autoCapitalize="off"
        spellCheck={false}
      />
      <TombolMataSandi tampil={tampil} onUbah={() => setTampil(balikTampil)} kontrol={id} />
    </div>
  );
}

/** Status mata berikutnya. Terpisah agar penjaganya dapat membuktikan arah baliknya. */
export function balikTampil(tampil: boolean): boolean {
  return !tampil;
}

export function TombolMataSandi({
  tampil,
  onUbah,
  kontrol,
}: {
  readonly tampil: boolean;
  readonly onUbah: () => void;
  readonly kontrol: string;
}): ReactNode {
  const teks = tampil ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi';
  return (
    <button
      type="button"
      className="isian-sandi-mata"
      onClick={onUbah}
      aria-label={teks}
      aria-pressed={tampil}
      aria-controls={kontrol}
      title={teks}
    >
      {/* Gaya garis sama dengan ikon BottomNav (viewBox 24, stroke 1.8). Dekoratif: nama tombol
          sudah dibawa aria-label, jadi ikon disembunyikan dari pembaca layar. */}
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d={tampil ? JALUR_MATA_TUTUP : JALUR_MATA} />
      </svg>
    </button>
  );
}

/** Jalur ikon sama dengan CRM & HBS Lab (b54c2f2f) agar matanya seragam lintas produk. */
export const JALUR_MATA =
  'M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12zM9 12a3 3 0 106 0 3 3 0 10-6 0';
export const JALUR_MATA_TUTUP =
  'M3 3l18 18M10.6 6.1A10.6 10.6 0 0112 6c6.4 0 10 6 10 6a17.8 17.8 0 01-3.2 3.9M6.6 6.6C3.8 8.4 2 12 2 12s3.6 6 10 6a9.8 9.8 0 005.4-1.6M9.9 9.9a3 3 0 004.2 4.2';
